import { useState, PropsWithChildren } from 'react'
import styles from 'features/contracts/add/components/producer-slider-layout/ProducerSliderLayout.module.scss'
import { ProducerSliderLayoutProps } from 'features/contracts/add/components/producer-slider-layout/types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useGetLanguage } from 'hooks/use-get-language'
import { AddContractSteps } from 'features/contracts/add/types'
import { useGetProductContentQuery } from 'store/queries/cms-api'
import PriceDisplay from 'features/contracts/add/components/price-display/PriceDisplay'
import { getProducerLocation } from 'utils/producer.ts'
import { Icon, Modal } from '@boltenergy-be/design-system'
import { getProductFromProductCode, LANGUAGES, PRODUCTS, SIMULATION_TYPES } from '@boltenergy-be/shared'

const ProducerSliderLayout = ({ children, contractData, currentStep }: PropsWithChildren<ProducerSliderLayoutProps>) => {
  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Redux queries
  const { data: productContent } = useGetProductContentQuery({ language })

  // Local state
  const [showMobileModal, setShowMobileModal] = useState<boolean>(false)

  // Constants
  const producer = contractData[AddContractSteps.PRODUCER]
  const { needsGas } = contractData[AddContractSteps.ADDRESS]
  const { simulationType, instalments } = contractData[AddContractSteps.INSTALMENT_DATA]
  const totalInstalment = (instalments.electricity || 0) + (needsGas ? instalments.gas || 0 : 0)
  const product = getProductFromProductCode(contractData[AddContractSteps.PRODUCT].electricity)
  const productName =
    [
      AddContractSteps.INSTALMENT_DATA,
      AddContractSteps.METER_DETAILS,
      AddContractSteps.CUSTOMER_DATA,
      AddContractSteps.CONFIRMATION
    ].includes(currentStep) && product
      ? `Bolt ${productContent?.[product]?.name}`
      : undefined
  const showPrice = [AddContractSteps.METER_DETAILS, AddContractSteps.CUSTOMER_DATA, AddContractSteps.CONFIRMATION].includes(currentStep)

  return (
    <>
      <button className={classNames(styles.card, { [styles['with-price']]: showPrice })} onClick={() => setShowMobileModal(true)}>
        <div className={styles['producer-container']}>
          <picture>
            <img src={producer?.images.profile?.url} alt="" />
            <div className={styles['energy-types']}>
              {needsGas && <Icon name="gas" className="text-accent-purple" />}
              <Icon name="electricity" className="text-primary" />
            </div>
          </picture>

          <div className={styles.producer}>
            <small>
              {t(($) => $.energyTypeAndLocation, {
                defaultValue: '{{ energyType }} uit {{ location }} van',

                energyType:
                  language === LANGUAGES.FR
                    ? t(($) => $.energyType[producer?.energyType || 'Solar']).toLowerCase()
                    : t(($) => $.energyType[producer?.energyType || 'Solar']),

                location: getProducerLocation(lowerCaseLanguage, producer?.location)
              })}
            </small>
            <h3>{producer?.name?.[lowerCaseLanguage]}</h3>
          </div>

          <span className={styles['open-icon']}>
            <Icon name="arrowTop" />
          </span>
        </div>

        {productName && (
          <>
            <hr />
            <div className={styles['price-container']}>
              <span className={styles.product}>{productName}</span>

              {showPrice && productName && simulationType !== SIMULATION_TYPES.eav && (
                <PriceDisplay
                  amount={totalInstalment}
                  label={
                    product === PRODUCTS.go
                      ? t(($) => $.perYear, {
                          ns: 'common'
                        })
                      : undefined
                  }
                />
              )}
            </div>
          </>
        )}
      </button>

      <Modal padding={false} className={styles.drawer} isOpen={showMobileModal} onRequestClose={() => setShowMobileModal(false)}>
        {children}
      </Modal>
    </>
  )
}

export default ProducerSliderLayout
